import { INSTANTIATOR_INTERFACES } from '../../model/interface';
import { IModelSource } from '../interface/db';
import IBaseDto from '../interface/IBaseDto';

/* Api data from serve response for type of Interface */
type ApiData<Interface extends keyof INSTANTIATOR_INTERFACES> = Parameters<
    INSTANTIATOR_INTERFACES[Interface]
>[0];

function _loadAll<
    Interface extends keyof INSTANTIATOR_INTERFACES,
    Constructor extends INSTANTIATOR_INTERFACES[Interface],
    Entity extends ReturnType<Constructor>,
>(
    db: IModelSource,
    tableName: Interface,
    ctor: (data: ApiData<Interface>) => Entity,
): Promise<Array<Entity>> {
    return new Promise(async function (resolve, reject) {
        try {
            const all = await db.all(tableName);
            const mapped = all.map(x => ctor(x));

            resolve(mapped);
        } catch (error) {
            reject(error);
        }
    });
}

function _load<
    Interface extends keyof INSTANTIATOR_INTERFACES,
    Constructor extends INSTANTIATOR_INTERFACES[Interface],
    Entity extends ReturnType<Constructor>,
>(
    db: IModelSource,
    tableName: Interface,
    id: number | string,
    ctor: (data: ApiData<Interface>) => Entity,
): Promise<Entity> {
    return new Promise(function (resolve, reject) {
        db.get(tableName, id)
            .then(x => {
                resolve(ctor(x));
            })
            .catch((error: Error) => reject(error));
    });
}

const module = {
    loadBackgroundModeId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IBackgroundModeDto', id, ctor as any),
    loadBeaconId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IBeaconDto', id, ctor as any),
    loadBeaconSensitivityId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IBeaconSensitivityDto', id, ctor as any),
    loadFloorplanId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IFloorPlanDto', id, ctor as any),
    loadFloorplanTranslationId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IFloorPlanTranslationDto', id, ctor as any),
    loadFontFamilyId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IFontFamilyDto', id, ctor as any),
    loadFontStyleId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IFontStyleDto', id, ctor as any),
    loadFontSizeUnitId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IFontSizeUnitDto', id, ctor as any),
    loadFontAlignmentId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IFontAlignmentDto', id, ctor as any),
    loadLanguageId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ILanguageDto', id, ctor as any),
    loadMediumId: <T>(db: IModelSource, guid: string, ctor: (data: any) => T) =>
        _load(db, 'IMediumDto', guid, ctor as any),
    loadMenuItemId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IMenuItemDto', id, ctor as any),
    loadPinTypeId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IPinTypeDto', id, ctor as any),
    loadScreenOrientationId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IScreenOrientationDto', id, ctor as any),
    loadSiteId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ISiteDto', id, ctor as any),
    loadSiteObjectId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ISiteObjectDto', id, ctor as any),
    loadSiteObjectOnFloorplanId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ISiteObjectOnFloorPlanDto', id, ctor as any),
    loadSiteObjectKeywordId: <T>(db: IModelSource, guid: string, ctor: (data: any) => T) =>
        _load(db, 'ISiteObjectKeywordDto', guid, ctor as any),
    loadSiteObjectTranslationId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ISiteObjectTranslationDto', id, ctor as any),
    loadSiteObjectTypeId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ISiteObjectTypeDto', id, ctor as any),
    loadSiteSupportedLanguageId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ISiteSupportedLanguageDto', id, ctor as any),
    loadStyleId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'IStyleDto', id, ctor as any),
    loadTourId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ITourDto', id, ctor as any),
    loadTourStatusId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ITourStatusDto', id, ctor as any),
    loadTourSubTypeId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ITourSubTypeDto', id, ctor as any),
    loadTourTranslations: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ITourTranslationDto', id, ctor as any),
    loadTourTypeId: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ITourTypeDto', id, ctor as any),
    loadTourSupportedLanguage: <T>(db: IModelSource, id: number, ctor: (data: any) => T) =>
        _load(db, 'ITourSupportedLanguageDto', id, ctor as any),

    allBackgroundModes: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IBackgroundModeDto', ctor as any),
    allBeacons: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IBeaconDto', ctor as any),
    allBeaconSensitivitys: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IBeaconSensitivityDto', ctor as any),
    allFloorplans: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IFloorPlanDto', ctor as any),
    allFloorplanTranslations: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IFloorPlanTranslationDto', ctor as any),
    allFontFamilys: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IFontFamilyDto', ctor as any),
    allFontStyles: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IFontStyleDto', ctor as any),
    allFontSizeUnits: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IFontSizeUnitDto', ctor as any),
    allFontAlignments: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IFontAlignmentDto', ctor as any),
    allLanguages: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ILanguageDto', ctor as any),
    allMediums: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IMediumDto', ctor as any),
    allMenuItems: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IMenuItemDto', ctor as any),
    allPinTypes: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IPinTypeDto', ctor as any),
    allScreenOrientations: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IScreenOrientationDto', ctor as any),
    allSites: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteDto', ctor as any),
    allSiteObjects: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteObjectDto', ctor as any),
    allSiteObjectOnFloorplan: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteObjectOnFloorPlanDto', ctor as any),
    allSiteObjectKeywords: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteObjectKeywordDto', ctor as any),
    allSiteObjectTranslations: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteObjectTranslationDto', ctor as any),
    allSiteObjectTypes: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteObjectTypeDto', ctor as any),
    allSiteSupportedLanguages: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ISiteSupportedLanguageDto', ctor as any),
    allStyles: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'IStyleDto', ctor as any),
    allTours: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ITourDto', ctor as any),
    allTourStatuss: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ITourStatusDto', ctor as any),
    allTourSubTypes: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ITourSubTypeDto', ctor as any),
    allTourTranslations: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ITourTranslationDto', ctor as any),
    allTourTypes: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ITourTypeDto', ctor as any),
    allTourSupportedLanguages: <T>(db: IModelSource, ctor: (data: any) => T) =>
        _loadAll(db, 'ITourSupportedLanguageDto', ctor as any),
};

export default module;
